import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnFiltersState,
  type Row,
  type SortingState,
  type Table,
} from '@tanstack/react-table';
import _unescape from 'lodash/unescape';

import { CaseStatuses } from 'utils/enums';
import { compareFn } from 'utils/sortByEstimatedTiming';

import { EntityType } from 'shared/constants';
import useEntityDialogState from 'shared/hooks/useEntityDialogState';
import useSelector from 'shared/hooks/useSelector';
import convertRenovationTypesToString from 'shared/utils/convertRenovationTypesToString';
import formatDatetime from 'shared/utils/formatDatetime';
import {
  createStatusOptionFilterFn,
  getStatusOptions,
} from 'shared/utils/grid';

import Dialog from 'shared/components/Dialog';
import Grid from 'shared/components/Grid';
import StatusView from 'shared/components/styled/SpaceRenovationRequestStatus';
import TableFilterComponent from 'shared/components/TableFilterComponent';
import AttachIcon from 'shared/icons/Attach';

import { NotificationBadge } from 'features/notification';

import type { UserSpaceRenovationRequest } from '../../types';
import { selectSearchedSpaceRenovationRequests } from '../../selectors';
import SpaceRenovationRequestViewForm from '../SpaceRenovationRequestViewForm';

const columnHelper = createColumnHelper<UserSpaceRenovationRequest>();

type SpaceRenovationRequestsGridProps = {
  isFetching: boolean;
};

const SpaceRenovationRequestsGrid = ({
  isFetching,
}: SpaceRenovationRequestsGridProps) => {
  const { t } = useTranslation();

  const spaceRenovationRequestsList = useSelector(
    selectSearchedSpaceRenovationRequests
  );

  const entityDialogState = useEntityDialogState();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'Status_Label', value: [] },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'CreatedOn', desc: true },
  ]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('Title', {
        header: 'Tunniste',
        cell: (info) => {
          const value = info.getValue();

          return (
            <NotificationBadge
              entityId={info.row.original.Id}
              entityType={EntityType.Repair}
            >
              {value && _unescape(value)}
            </NotificationBadge>
          );
        },
        enableColumnFilter: false,
        meta: {
          sx: { width: '15%' },
        },
      }),
      columnHelper.accessor('Space.Name', {
        header: t('##attribute#incident#uds_spaceid'),
        sortingFn: 'alphanumeric',
        meta: {
          sx: { width: '10%' },
        },
      }),
      columnHelper.accessor('RenovationTypes', {
        header: 'Muutostyön tyyppi',
        sortingFn: (a, b) => {
          const aOriginalValue = convertRenovationTypesToString(
            a.original.RenovationTypes
          );
          const bOriginalValue = convertRenovationTypesToString(
            b.original.RenovationTypes
          );
          return aOriginalValue.localeCompare(bOriginalValue);
        },
        cell: (info) => convertRenovationTypesToString(info.getValue()),
        meta: { sx: { width: '22%' } },
      }),
      columnHelper.accessor('EstimatedTiming', {
        header: 'Arvioitu ajoitus',
        sortingFn: (a, b) => compareFn(a.original, b.original),
        meta: { sx: { width: '18%' } },
      }),
      columnHelper.accessor('Status.Label', {
        header: 'Palvelupyynnön tila',
        cell: (info) => {
          const { Status } = info.row.original;

          if (!Status) return null;

          switch (Status.Value) {
            case CaseStatuses.Approved:
            case CaseStatuses.Completed:
              return <StatusView success>{Status.Label}</StatusView>;
            case CaseStatuses.Rejected:
              return <StatusView error>{Status.Label}</StatusView>;
            default:
              return Status.Label;
          }
        },
        filterFn: createStatusOptionFilterFn<UserSpaceRenovationRequest>(),
        meta: {
          sx: { width: '15%' },
          filterComponent: ({
            column,
            table,
          }: {
            column: Column<UserSpaceRenovationRequest, unknown>;
            table: Table<UserSpaceRenovationRequest>;
          }) => (
            <TableFilterComponent
              column={column}
              table={table}
              selectAllByDefault
              showSelectAllOption
              selectAllLabel={'Kaikki tilat'}
              selectOptions={getStatusOptions}
            />
          ),
        },
      }),
      columnHelper.accessor('HasFiles', {
        enableSorting: false,
        header: t('#portal.incident.grid.label.attachments'),
        cell: (info) =>
          info.getValue() && <AttachIcon sx={{ fontSize: '1.3rem' }} />,
        meta: { align: 'center', sx: { width: '10%' } },
      }),
      columnHelper.accessor('CreatedOn', {
        header: 'Luotu',
        sortingFn: 'datetime',
        cell: (info) => formatDatetime(info.getValue()),
        meta: {
          sx: { width: '10%' },
        },
      }),
    ],
    [t]
  );

  const table = useReactTable({
    data: spaceRenovationRequestsList,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  const handleClickRow = (
    _event: MouseEvent,
    row: Row<UserSpaceRenovationRequest>
  ) => {
    entityDialogState.onOpen(row.original.Id);
  };

  return (
    <>
      <Grid isLoading={isFetching} table={table} onClickRow={handleClickRow} />
      <Dialog
        open={entityDialogState.open}
        maxWidth="lg"
        fullWidth
        onClose={entityDialogState.onClose}
        TransitionProps={{
          onExited: entityDialogState.onClearId,
        }}
      >
        <SpaceRenovationRequestViewForm caseId={entityDialogState.id!} />
      </Dialog>
    </>
  );
};

export default SpaceRenovationRequestsGrid;
